import _toArray from "@babel/runtime/helpers/toArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { fromJS, List, Map } from "immutable";
import { isDayInRange, parseDate } from "../util/date.js";
import { dateTimeToTime, isoStringToTime, isTimeInRange, isTimeSame, isTimeSameOrAfter, isTimeSameOrBefore } from "../util/time.js";
import { bookingRuleTypes, openingTimeTypes } from "./constants.js";
var MIN_LEAD_TIME = bookingRuleTypes.MIN_LEAD_TIME,
  MAX_LEAD_TIME = bookingRuleTypes.MAX_LEAD_TIME,
  MIN_BOOKING_PERIOD = bookingRuleTypes.MIN_BOOKING_PERIOD,
  MAX_BOOKING_PERIOD = bookingRuleTypes.MAX_BOOKING_PERIOD;

// When a station has no opening times (or no station is selected) we want to
// limit the time selection to values between 06:00—20:00.
var defaultOpeningTime = fromJS({
  type: openingTimeTypes.OPEN,
  fromTime: isoStringToTime("1900-01-01T06:00"),
  tillTime: isoStringToTime("1900-01-01T20:00")
});

// Setting the minBookingPeriod to 1 minute is necessary in order to correctly
// disable a day in the CI calendar when the last possible time slot is selected
// as CO dateTime.
var defaultMinBookingPeriod = fromJS({
  type: MIN_BOOKING_PERIOD,
  valueInMinutes: 1
});
export var findStation = function findStation(stations, stationId) {
  return stations === null || stations === void 0 ? void 0 : stations.find(function (station) {
    return station.get("id") === stationId;
  });
};
var stationToBookingRule = function stationToBookingRule(type, defaultRule) {
  return function (station) {
    return (station || Map()).get("bookingRules", List()).find(function (rule) {
      return rule.get("type") === type;
    }) || defaultRule;
  };
};
export var stationToMinLeadTimeRule = stationToBookingRule(MIN_LEAD_TIME);
export var stationToMaxLeadTimeRule = stationToBookingRule(MAX_LEAD_TIME);
export var stationToMinBookingPeriodRule = stationToBookingRule(MIN_BOOKING_PERIOD, defaultMinBookingPeriod);
export var stationToMaxBookingPeriodRule = stationToBookingRule(MAX_BOOKING_PERIOD);
export var stationToOpeningTimes = function stationToOpeningTimes(station) {
  var openingTimes = (station || Map()).get("openingTimes");
  return openingTimes && openingTimes.size > 0 ? openingTimes : List([defaultOpeningTime]);
};
var appliesToWeekday = function appliesToWeekday(dateTime) {
  return function (openingTime) {
    var weekdayFrom = openingTime.get("weekdayFrom");
    var weekdayTill = openingTime.get("weekdayTill");
    var weekday = dateTime.getDay() + 1;
    return weekdayFrom && weekdayTill ? weekday >= weekdayFrom && weekday <= weekdayTill : true;
  };
};
var appliesToDateRange = function appliesToDateRange(dateTime) {
  return function (openingTime) {
    var fromDate = parseDate(openingTime.get("fromDate"));
    var tillDate = parseDate(openingTime.get("tillDate"));
    return !fromDate || !tillDate || isDayInRange(dateTime, fromDate, tillDate);
  };
};
var minTime = function minTime(t1, t2) {
  return isTimeSameOrBefore(t1, t2) ? t1 : t2;
};
var maxTime = function maxTime(t1, t2) {
  return isTimeSameOrAfter(t1, t2) ? t1 : t2;
};
var hasOverlap = function hasOverlap(_ref, _ref2) {
  var _ref3 = _slicedToArray(_ref, 2),
    start1 = _ref3[0],
    end1 = _ref3[1];
  var _ref4 = _slicedToArray(_ref2, 2),
    start2 = _ref4[0],
    end2 = _ref4[1];
  return isTimeSameOrBefore(start1, start2) && isTimeSameOrAfter(end1, start2) || isTimeSameOrBefore(start2, start1) && isTimeSameOrAfter(end2, start1);
};
var isContainedWithin = function isContainedWithin(_ref5, _ref6) {
  var _ref7 = _slicedToArray(_ref5, 2),
    start1 = _ref7[0],
    end1 = _ref7[1];
  var _ref8 = _slicedToArray(_ref6, 2),
    start2 = _ref8[0],
    end2 = _ref8[1];
  return isTimeSameOrAfter(start1, start2) && isTimeSameOrBefore(end1, end2);
};
var sortRangesByStartTime = function sortRangesByStartTime(_ref9, _ref10) {
  var _ref11 = _slicedToArray(_ref9, 1),
    start1 = _ref11[0];
  var _ref12 = _slicedToArray(_ref10, 1),
    start2 = _ref12[0];
  return isTimeSame(start1, start2) ? 0 : isTimeSameOrBefore(start1, start2) ? -1 : /* else */1;
};
var mergeRanges = function mergeRanges(_ref13, _ref14) {
  var _ref15 = _slicedToArray(_ref13, 2),
    start1 = _ref15[0],
    end1 = _ref15[1];
  var _ref16 = _slicedToArray(_ref14, 2),
    start2 = _ref16[0],
    end2 = _ref16[1];
  return [minTime(start1, start2), maxTime(end1, end2)];
};
var normalizeRanges = function normalizeRanges(ranges) {
  return _toConsumableArray(ranges) // make a copy b/c `sort` is destructive
  .sort(sortRangesByStartTime).reduce(function (normalizedRanges, range) {
    var _normalizedRanges = _toArray(normalizedRanges),
      last = _normalizedRanges[0],
      rest = _normalizedRanges.slice(1);
    if (!last) {
      return [range];
    } else if (hasOverlap(range, last)) {
      return [mergeRanges(range, last)].concat(_toConsumableArray(rest));
    } else {
      return [range, last].concat(_toConsumableArray(rest));
    }
  }, []).reverse();
};
var subtractRange = function subtractRange(ranges, rangeToSubtract) {
  return normalizeRanges(ranges.reduce(function (acc, range) {
    if (!hasOverlap(range, rangeToSubtract)) {
      return [].concat(_toConsumableArray(acc), [range]);
    } else if (isContainedWithin(range, rangeToSubtract)) {
      return acc;
    } else if (isContainedWithin(rangeToSubtract, range)) {
      return [].concat(_toConsumableArray(acc), [[range[0], rangeToSubtract[0]], [rangeToSubtract[1], range[1]]]);
    } else {
      return isTimeSameOrBefore(range[0], rangeToSubtract[0]) ? [].concat(_toConsumableArray(acc), [[range[0], rangeToSubtract[0]]]) : [].concat(_toConsumableArray(acc), [[rangeToSubtract[1], range[1]]]);
    }
  }, []));
};
export var applyOpeningTime = function applyOpeningTime(timeRanges, openingTime) {
  var fromTime = openingTime.get("fromTime");
  var tillTime = openingTime.get("tillTime");
  var range = [fromTime ? fromTime.toJS() : isoStringToTime("1900-01-01T00:00Z"), tillTime ? tillTime.toJS() : isoStringToTime("1900-01-01T24:00Z")];
  switch (openingTime.get("type")) {
    case openingTimeTypes.OPEN:
      return normalizeRanges([].concat(_toConsumableArray(timeRanges), [range]));
    case openingTimeTypes.CLOSED:
      return subtractRange(timeRanges, range);
    default:
      return timeRanges;
  }
};
export var isStationOpenAtDate = function isStationOpenAtDate(openingTimes, date) {
  if (!openingTimes || openingTimes.size === 0) {
    return true;
  }
  var timeRanges = openingTimes.sort(function (a, b) {
    if (a.get("type") === b.get("type")) {
      return 0;
    }

    // Sort items so that 'OPEN' entries come before 'CLOSED'
    return a.get("type") === openingTimeTypes.CLOSED ? 1 : -1;
  }).filter(appliesToWeekday(date)).filter(appliesToDateRange(date)).reduce(applyOpeningTime, []);
  return timeRanges.length > 0;
};
export var isStationOpen = function isStationOpen(openingTimes, dateTime) {
  if (!openingTimes || openingTimes.size === 0) {
    return true;
  }
  var timeRanges = openingTimes.filter(appliesToWeekday(dateTime)).filter(appliesToDateRange(dateTime)).reduce(applyOpeningTime, []);
  return timeRanges.some(function (_ref17) {
    var _ref18 = _slicedToArray(_ref17, 2),
      start = _ref18[0],
      end = _ref18[1];
    return isTimeInRange(dateTime, start, end);
  });
};
export var isStationOpenAtDateAfterGivenTime = function isStationOpenAtDateAfterGivenTime(openingTimes, dateTime) {
  if (openingTimes === undefined || openingTimes.size === 0) {
    return true;
  }
  var timeRanges = openingTimes.filter(appliesToWeekday(dateTime)).filter(appliesToDateRange(dateTime)).reduce(applyOpeningTime, []);
  return timeRanges.some(function (_ref19) {
    var _ref20 = _slicedToArray(_ref19, 2),
      start = _ref20[0],
      end = _ref20[1];
    return isTimeSameOrBefore(dateTimeToTime(dateTime), end);
  });
};
export var isStationOpenAtDateBeforeGivenTime = function isStationOpenAtDateBeforeGivenTime(openingTimes, dateTime) {
  if (openingTimes === undefined || openingTimes.size === 0) {
    return true;
  }
  var timeRanges = openingTimes.filter(appliesToWeekday(dateTime)).filter(appliesToDateRange(dateTime)).reduce(applyOpeningTime, []);
  return timeRanges.some(function (_ref21) {
    var _ref22 = _slicedToArray(_ref21, 2),
      start = _ref22[0],
      end = _ref22[1];
    return isTimeSameOrAfter(dateTimeToTime(dateTime), start);
  });
};